"use client";

import React, { useState, useMemo } from "react";
import { ArrowRight, ArrowLeft, CircleCheck, Send, User, Building2, Globe, Wallet, MessageSquare, Phone, Mail } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RangeInput } from "@/components/ui/range-input";
import { Toaster } from "@/components/ui/sonner";
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(({
  className,
  type,
  ...props
}, ref) => {
  return <input type={type} className={`flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const BUDGET_OPTIONS = ["Wassen & Knippen", "Knippen & Föhnen", "Kleur & Highlights", "Balayage Specialist"];
const WEBSITE_TYPES = ["Sophie (Master Stylist)", "Emma (Top Stylist)", "Lisa (Stylist)", "Geen voorkeur"];
function ConfiguratorForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    formState: {
      errors
    }
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 2;
  const nextStep = async () => {
    setStep(2);
  };
  const prevStep = () => {
    setStep(1);
  };
  if (isSubmitted && !submitError) {
    return <div data-section-id="61137" className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-green-500/20 flex items-center justify-center border border-green-500/50 shadow-[0_0_30px_-5px_rgba(34,197,94,0.4)]">
          <CircleCheck className="size-10 text-green-500" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif font-bold text-[#2C2C2C]">Afspraak Bevestigd!</h3>
          <p className="text-[#6B7280] font-sans max-w-md mx-auto">
            {successMessage || "Bedankt voor uw boeking. We hebben uw aanvraag ontvangen en sturen direct een bevestiging naar uw e-mailadres."}
          </p>
        </div>
        <Button variant="outline" onClick={() => window.location.reload()} className="mt-4 border-[#CBBDBD]/20 hover:bg-[#CBBDBD]/10 hover:text-[#CBBDBD]">
          Nog een afspraak maken
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <div className="relative mb-8">
        <div className="absolute top-1/2 left-0 w-full h-1 bg-[#E5E7EB] rounded-full -z-10"></div>
        <div className="absolute top-1/2 left-0 h-1 bg-[#CBBDBD] rounded-full -z-10 transition-all duration-500 ease-out" style={{
        width: `${(step - 1) / (totalSteps - 1) * 100}%`
      }}></div>
        <div className="flex justify-between w-full">
          {[1, 2].map(s => <div key={s} className={`flex items-center justify-center size-8 rounded-full border-2 transition-all duration-300 ${step >= s ? "bg-[#CBBDBD] border-[#CBBDBD] text-white shadow-[0_0_15px_rgba(203,189,189,0.5)]" : "bg-white border-[#E5E7EB] text-[#6B7280]"}`}>
              {step > s ? <CircleCheck className="size-5" /> : s}
            </div>)}
        </div>
        <div className="flex justify-between w-full mt-2 text-xs font-medium text-[#6B7280] px-1 font-sans">
          <span>Uw Gegevens</span>
          <span>Behandeling & Stylist</span>
        </div>
      </div>

      {}
      <div className={step === 1 ? "block animate-in slide-in-from-left-4 fade-in duration-300" : "hidden"}>
        <div className="grid gap-6 md:grid-cols-2">
          <FormField control={control} name="naam" rules={{
          required: "Naam is verplicht"
        }} render={({
          field
        }) => <FormItem className="col-span-2 md:col-span-1">
                <FormLabel className="text-[#2C2C2C] font-sans">Volledige Naam <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 size-4 text-[#6B7280]" />
                    <Input placeholder="Uw naam" className="pl-9 bg-[#F9FAFB] border-[#E5E7EB]" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="bedrijfsnaam" render={({
          field
        }) => <FormItem className="col-span-2 md:col-span-1">
                <FormLabel className="text-[#2C2C2C] font-sans">Eventuele Opmerkingen</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-2.5 size-4 text-[#6B7280]" />
                    <Input placeholder="Bijv. allergieën of wensen" className="pl-9 bg-[#F9FAFB] border-[#E5E7EB]" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "E-mailadres is verplicht",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Ongeldig e-mailadres"
          }
        }} render={({
          field
        }) => <FormItem className="col-span-2 md:col-span-1">
                <FormLabel className="text-[#2C2C2C] font-sans">E-mailadres <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 size-4 text-[#6B7280]" />
                    <Input type="email" placeholder="uw@email.com" className="pl-9 bg-[#F9FAFB] border-[#E5E7EB]" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="telefoon" rules={{
          required: "Telefoonnummer is verplicht"
        }} render={({
          field
        }) => <FormItem className="col-span-2 md:col-span-1">
                <FormLabel className="text-[#2C2C2C] font-sans">Telefoonnummer <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 size-4 text-[#6B7280]" />
                    <Input type="tel" placeholder="06 1234 5678" className="pl-9 bg-[#F9FAFB] border-[#E5E7EB]" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className={step === 2 ? "block animate-in slide-in-from-right-4 fade-in duration-300" : "hidden"}>
        <div className="space-y-6">
          <FormField control={control} name="website_type" rules={{
          required: "Selecteer een stylist"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-[#2C2C2C] font-sans">Kies uw Stylist <span className="text-destructive">*</span></FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full pl-9 relative bg-[#F9FAFB] border-[#E5E7EB]">
                      <Globe className="absolute left-3 top-2.5 size-4 text-[#6B7280] z-10" />
                      <SelectValue placeholder="Selecteer een expert" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {WEBSITE_TYPES.map(type => <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="budget" render={({
          field
        }) => <FormItem className="space-y-4">
                <div className="flex justify-between items-center">
                  <FormLabel className="text-[#2C2C2C] font-sans">Gewenste Behandeling</FormLabel>
                  <span className="text-sm font-medium text-[#CBBDBD] bg-[#CBBDBD]/10 px-3 py-1 rounded-full border border-[#CBBDBD]/20">
                    {field.value || "Selecteer behandeling"}
                  </span>
                </div>
                <FormControl>
                  <div className="pt-2 px-1">
                    <RangeInput min={0} max={BUDGET_OPTIONS.length - 1} step={1} value={BUDGET_OPTIONS.indexOf(field.value) !== -1 ? BUDGET_OPTIONS.indexOf(field.value) : 0} onValueChange={val => field.onChange(BUDGET_OPTIONS[val])} className="accent-[#CBBDBD]" />
                    <div className="flex justify-between mt-2 text-xs text-[#6B7280] font-sans">
                      <span>Basis</span>
                      <span>Exclusief</span>
                    </div>
                  </div>
                </FormControl>
                <FormDescription className="text-xs text-[#6B7280]">
                  Sleep de schuif om uw gewenste pakket te selecteren.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="extra_informatie" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-[#2C2C2C] font-sans">Extra informatie over uw haar</FormLabel>
                <FormControl>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 size-4 text-[#6B7280]" />
                    <Textarea placeholder="Beschrijf hier uw huidige haartype of specifieke wensen voor uw bezoek." className="min-h-[120px] pl-9 resize-none bg-[#F9FAFB] border-[#E5E7EB]" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="flex justify-between pt-4 border-t border-[#E5E7EB]">
        {step === 1 ? <div className="w-full flex justify-end">
            <Button type="button" onClick={nextStep} className="w-full md:w-auto group bg-[#CBBDBD] hover:bg-[#2C2C2C] text-white transition-colors duration-400">
              Volgende Stap
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div> : <>
            <Button type="button" variant="outline" onClick={prevStep} className="group border-[#E5E7EB] text-[#6B7280] hover:bg-[#F9FAFB]">
              <ArrowLeft className="mr-2 size-4 transition-transform group-hover:-translate-x-1" />
              Vorige
            </Button>
            <Button type="submit" disabled={isSubmitting} className="min-w-[140px] bg-[#CBBDBD] hover:bg-[#2C2C2C] text-white transition-colors duration-400">
              {isSubmitting ? "Verwerken..." : <>
                  Boeking Voltooien
                  <Send className="ml-2 size-4" />
                </>}
            </Button>
          </>}
      </div>

      {submitError && <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2">
          <span className="font-bold">Fout:</span> {submitError}
        </div>}
    </Form>;
}
export default function OfferteConfigurator() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="boek-jouw-moment" className="bg-[#F7F7F7] text-[#2C2C2C] py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[#CBBDBD]/10 rounded-full blur-[100px] opacity-40"></div>
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-[#EDE6DE]/30 rounded-full blur-[100px] opacity-40"></div>
      </div>

      <div className="container mx-auto max-w-4xl relative z-10">
        <div ref={ref} className={`transition-all duration-700 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-4xl md:text-6xl font-serif font-bold tracking-tight text-[#2C2C2C]">
              Boek Jouw Moment
            </h2>
            <p className="text-[#6B7280] font-sans text-lg max-w-2xl mx-auto">
              Plan eenvoudig uw bezoek aan onze salon in Teuge. Kies uw favoriete stylist en de gewenste behandeling voor een ervaring van pure luxe.
            </p>
          </div>

          <Card className="border-[#E5E7EB] bg-white backdrop-blur-xl shadow-sm overflow-hidden rounded-lg">
            <CardHeader className="border-b border-[#E5E7EB] bg-[#F9FAFB] px-6 py-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#CBBDBD]/20 rounded-lg border border-[#CBBDBD]/30">
                  <Wallet className="size-6 text-[#CBBDBD]" />
                </div>
                <div>
                  <CardTitle className="text-xl font-serif text-[#2C2C2C]">Online Reservering</CardTitle>
                  <CardDescription className="font-sans">Stap voor stap naar uw nieuwe look bij VIP Company</CardDescription>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-6 md:p-8">
              <FormProvider formKey="boek_moment_form_home" formId="boek_moment_form_home" sectionName="offerte-configurator" defaultValues={{
              naam: "",
              email: "",
              telefoon: "",
              bedrijfsnaam: "",
              website_type: "",
              budget: BUDGET_OPTIONS[0],
              extra_informatie: ""
            }} validationRules={{
              naam: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              telefoon: {
                required: true,
                pattern: "phone"
              },
              website_type: {
                required: true
              }
            }} submitText="Boeking Voltooien" successMessage="Bedankt! Uw afspraak is succesvol ingepland. We sturen binnen enkele minuten een bevestiging." errorMessage="Er is iets misgegaan bij het verwerken van uw boeking. Probeer het later opnieuw.">
                <ConfiguratorForm />
              </FormProvider>
            </CardContent>
            
            <CardFooter className="bg-[#F9FAFB] border-t border-[#E5E7EB] px-6 py-4 text-xs text-[#6B7280] flex justify-center text-center font-sans">
              <p>
                Door deze boeking te voltooien gaat u akkoord met onze privacyverklaring. 
                Uw gegevens worden vertrouwelijk behandeld.
              </p>
            </CardFooter>
          </Card>
        </div>
      </div>
      <Toaster />
    </section>;
}